// src/App.jsx
import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useTranslation } from 'react-i18next';
import './i18n';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams, useNavigate, useLocation } from "react-router-dom";
import {
  getToken,
  setToken,
  getUserData,
  setUserData,
  removeToken,
  removeUserData,
  logoutUser as apiLogoutUser,
  getProfile as fetchUserProfileAPI,
  loginUser as apiLoginUser,
  getInstitutionBySubdomain,
  getCalendarSessions,
  getChatContacts,
  markMessagesAsRead
} from "./services/api";

// Add date-fns imports for calendar calculations
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek
} from 'date-fns';

// Component Imports
import InstitutionLogin from "./components/InstitutionLogin";
import Calendar from "./components/dashboard/Calendar";
import ProfileComponent from "./components/dashboard/ProfileComponent";
import StudentsOverview from "./components/dashboard/StudentsOverview";
import CourseList from "./components/courses/CourseList";
import SessionList from "./components/sessions/SessionList";

// Session Components
import SessionsPage from "./pages/sessions";
import LiveSessionMain from "./pages/sessions/components/LiveSession/LiveSessionMain";
import DetachedWhiteboardPage from "./pages/sessions/components/LiveSession/whiteboard/DetachedWhiteboardPage";

import SubdomainEntry from "./components/SubdomainEntry";
import CompleteInvitationSetup from "./components/auth/CompleteInvitationSetup";
import ResetPassword from "./components/auth/ResetPassword";
import ProtectedRoute from "./components/common/ProtectedRoute";

// Store imports
import useSessionStore from "./stores/sessionStore";
import useAuthStore from "./stores/authStore";

// NEW: WebSocket Provider and hooks
import { WebSocketProvider } from "./contexts/WebSocketContext";
import { useWebSocket } from "./hooks/useWebSocket";
import { toast } from 'react-hot-toast';

// NEW: App Layout Component
import AppLayout from "./components/layouts/AppLayout";

const APP_VERSION = "1.0.3-beta";

// Helper function to extract subdomain from production URL
function getSubdomain() {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  if (hostname === 'localhost' || hostname.match(/^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/)) {
    return null;
  }
  
  if (parts.length >= 3) {
    return parts[0];
  }
  
  return null;
}

// Institution Error Page Component
const InstitutionErrorPage = React.memo(() => {
  return (
    <div 
      style={{
        margin: 0,
        padding: 0,
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        background: 'linear-gradient(to right, #f1f5f9, #ffffff)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        color: '#334155',
        textAlign: 'center',
        flexDirection: 'column'
      }}
    >
      <h1 
        style={{
          fontSize: '4rem',
          marginBottom: '1rem',
          background: 'linear-gradient(to right, #06b6d4, #22d3ee)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        404
      </h1>
      <p 
        style={{
          fontSize: '1.25rem',
          color: '#64748b'
        }}
      >
        Page not found.
      </p>
    </div>
  );
});

// CRITICAL FIX: Move CourseSessionsWrapper OUTSIDE of AppContent
const CourseSessionsWrapper = memo(({ 
  sessionType, 
  user, 
  userRole, 
  institutionName 
}) => {
  const { t } = useTranslation();
  const { courseId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [courseName, setCourseName] = useState(location.state?.courseName || '');
  
  useEffect(() => {
    const pageName = sessionType === 'upcoming' ? t('sessions.upcomingSessions') : t('sessions.completedSessions');
    document.title = `${institutionName || t('app.defaultSchool')} - ${pageName}`;
  }, [sessionType, institutionName, t]);
  
  const handleCourseNameLoad = useCallback((name) => {
    setCourseName(name);
  }, []);

  return (
    <SessionList
      user={user}
      userRole={userRole}
      initialTab={sessionType}
      onCourseNameLoad={handleCourseNameLoad}
      institutionName={institutionName}
    />
  );
}, (prevProps, nextProps) => {
  // Only re-render if these specific props change
  return (
    prevProps.sessionType === nextProps.sessionType &&
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.userRole === nextProps.userRole &&
    prevProps.institutionName === nextProps.institutionName
  );
});

CourseSessionsWrapper.displayName = 'CourseSessionsWrapper';

// Main App Content Component (to be wrapped by WebSocketProvider)
function AppContent() {
  const { t, i18n } = useTranslation();
  
  // Auth and user state
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserRoleInSelectedInstitution, setCurrentUserRoleInSelectedInstitution] = useState(null);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginEmail, setLoginEmail] = useState('');

  // Institution state
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isLoadingInstitution, setIsLoadingInstitution] = useState(false);

  // FIXED: Track actual message counts, not booleans
  const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
  const [userUnreadCounts, setUserUnreadCounts] = useState({}); // Store COUNTS per user
  const [lastReadMessageIds, setLastReadMessageIds] = useState({}); // Track last read message ID per user
  const [initialUnreadLoaded, setInitialUnreadLoaded] = useState(false); // Track if initial load is done

  // Store hooks
  const { resetSessionStore } = useSessionStore();
  const authStore = useAuthStore();
  
  // NEW: WebSocket hooks for global chat listener
  const { addMessageHandler, isConnected, sendMessage: wsSendMessage } = useWebSocket();

  // FIXED: Prevent multiple initializations and add logout state
  const [hasInitialized, setHasInitialized] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // FIXED: Track which messages have been marked as read in session
  const sessionReadMessagesRef = React.useRef(new Set());

  // CRITICAL FIX: Create stable callbacks with empty deps
  const stableMarkMessagesAsReadForUser = useCallback((userId) => {
    const userIdStr = String(userId);
    
    // FORCE clear from localStorage immediately
    const storageKey = `unread_${userIdStr}`;
    localStorage.removeItem(storageKey);
    
    // Also clear from the main unreadMessages object
    const currentUnread = JSON.parse(localStorage.getItem('unreadMessages') || '{}');
    delete currentUnread[userIdStr];
    localStorage.setItem('unreadMessages', JSON.stringify(currentUnread));
    
    // Clear from session tracking
    sessionReadMessagesRef.current.delete(userIdStr);
    
    // Update state
    setUserUnreadCounts(prev => {
      const newCounts = { ...prev };
      delete newCounts[userIdStr];
      
      // Recalculate total
      const newTotal = Object.values(newCounts).reduce((sum, count) => sum + count, 0);
      setGlobalUnreadCount(newTotal);
      
      return newCounts;
    });
  }, []); // Empty deps - never changes

  const stableClearUserFromSessionTracking = useCallback((userId) => {
    const userIdStr = String(userId);
    
    // AGGRESSIVE localStorage clearing
    localStorage.removeItem(`unread_${userIdStr}`);
    
    // Clear from main storage
    const currentUnread = JSON.parse(localStorage.getItem('unreadMessages') || '{}');
    delete currentUnread[userIdStr];
    localStorage.setItem('unreadMessages', JSON.stringify(currentUnread));
    
    // Clear from session tracking
    sessionReadMessagesRef.current.delete(userIdStr);
    
    // Clear from state
    setUserUnreadCounts(prev => {
      const newCounts = { ...prev };
      delete newCounts[userIdStr];
      return newCounts;
    });
  }, []); // Empty deps - never changes

  // Stable callback for unread count change
  const handleUnreadCountChange = useCallback((count) => {
    setGlobalUnreadCount(count);
  }, []);

  // FIXED: Global functions for syncing with PersonalChatInSession and ChatWindow
  useEffect(() => {
    // Function to update global count from session
    window.updateGlobalUnreadCount = (count, source) => {
      if (source === 'session') {
        setGlobalUnreadCount(count);
      }
    };

    // Function to mark messages as read globally
    window.markMessagesReadGlobally = (userId) => {
      stableMarkMessagesAsReadForUser(userId);
    };

    // Function to clear session tracking when leaving session
    window.clearSessionTracking = () => {
      sessionReadMessagesRef.current.clear();
    };

    // Function to get unread count for a specific user
    window.getUnreadCountForUser = (userId) => {
      const counts = JSON.parse(localStorage.getItem('unreadMessages') || '{}');
      return counts[userId] || 0; // Return actual count
    };

    // FIXED: Global refresh function that properly counts unread
    window.refreshUnreadCount = async () => {
      try {
        const response = await getChatContacts();
        if (response.success && Array.isArray(response.data)) {
          const unreadByUser = {};
          let totalUnreadMessages = 0;
          
          response.data.forEach(contact => {
            // Skip if marked as read in session
            if (!sessionReadMessagesRef.current.has(String(contact.id))) {
              const unreadCount = contact.unread || 0;
              if (unreadCount > 0) {
                unreadByUser[contact.id] = unreadCount; // Store actual count
                totalUnreadMessages += unreadCount;
              }
            }
          });
          
          setGlobalUnreadCount(totalUnreadMessages);
          setUserUnreadCounts(unreadByUser);
          
          // Update localStorage
          localStorage.setItem('unreadMessages', JSON.stringify(unreadByUser));
          
          // Update last read message IDs
          const lastReadIds = {};
          response.data.forEach(contact => {
            if (contact.lastReadMessageId) {
              lastReadIds[contact.id] = contact.lastReadMessageId;
            }
          });
          setLastReadMessageIds(lastReadIds);
          localStorage.setItem('lastReadMessageIds', JSON.stringify(lastReadIds));
        }
      } catch (error) {
        // Silent error handling
      }
    };

    return () => {
      delete window.updateGlobalUnreadCount;
      delete window.markMessagesReadGlobally;
      delete window.clearSessionTracking;
      delete window.refreshUnreadCount;
      delete window.getUnreadCountForUser;
    };
  }, [stableMarkMessagesAsReadForUser]);

  // FIXED: Global chat listener with proper message counting
  useEffect(() => {
    if (!isConnected || !currentUser || !initialUnreadLoaded) return;

    const userId = currentUser?.id || currentUser?.userId;
    if (!userId) return;

    // Subscribe to chat updates globally
    wsSendMessage({
      type: 'subscribe_chat',
      userId: userId
    });

    // Listen for direct messages globally
    const unsubscribeDirect = addMessageHandler('direct_message', (data) => {
      const messageData = data.message || {};
      const senderId = data.senderId || data.sender_id;
      const receiverId = data.receiverId || data.receiver_id || messageData.receiver_user_id;
      const currentUserId = String(userId);
      
      // Check if this is an incoming message for the current user
      if (String(receiverId) === currentUserId && String(senderId) !== currentUserId) {
        // Check if we're in a live session
        const isInLiveSession = window.location.pathname.includes('/sessions/');
        
        // Check if PersonalChatInSession is viewing this conversation
        const personalChatElement = document.querySelector('[data-viewing-user-id]');
        const viewingUserId = personalChatElement?.getAttribute('data-viewing-user-id');
        const isInSessionChat = document.querySelector('[data-session-chat="personal"]');
        
        if (isInLiveSession && isInSessionChat && viewingUserId === String(senderId)) {
          return;
        }
        
        // Check if ChatWindow is open and viewing this conversation
        const chatWindowElement = document.querySelector('[data-chat-viewing-user-id]');
        const chatViewingUserId = chatWindowElement?.getAttribute('data-chat-viewing-user-id');
        
        if (chatViewingUserId === String(senderId)) {
          return;
        }
        
        // Check if this sender was marked as read in session or cleared
        const senderIdStr = String(senderId);
        if (!sessionReadMessagesRef.current.has(senderIdStr)) {
          // Get current counts from localStorage - FRESH READ
          const storedUnread = JSON.parse(localStorage.getItem('unreadMessages') || '{}');
          
          // Check if user was cleared (not in localStorage)
          if (!storedUnread.hasOwnProperty(senderIdStr)) {
            // User was cleared, start from 0
            storedUnread[senderIdStr] = 1;
          } else {
            // INCREMENT the count for this user
            storedUnread[senderIdStr] = (storedUnread[senderIdStr] || 0) + 1;
          }
          
          localStorage.setItem('unreadMessages', JSON.stringify(storedUnread));
          
          // Update state
          setUserUnreadCounts(prev => {
            const newCounts = { ...prev, [senderIdStr]: storedUnread[senderIdStr] };
            
            // Calculate total unread messages (sum of all counts)
            const total = Object.values(newCounts).reduce((sum, count) => sum + count, 0);
            setGlobalUnreadCount(total);
            
            return newCounts;
          });
          
          // Show toast notification for EVERY new message
          const senderName = messageData.sender_name || data.senderName || t('videoGrid.someone');
          const messageText = messageData.message || messageData.messageContent || '';
          const previewText = messageText.length > 50 
            ? messageText.substring(0, 47) + '...' 
            : messageText;
          
          toast.success(
            <div>
              <strong>{senderName}</strong>
              <div className="text-sm">{previewText}</div>
            </div>,
            {
              duration: 4000,
              position: 'bottom-right',
              style: {
                background: '#fff',
                color: '#333',
                padding: '12px',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              }
            }
          );
          
          // Play notification sound
          try {
            const audio = new Audio('/notification.mp3');
            audio.volume = 0.5;
            audio.play().catch(e => {});
          } catch (e) {
            // Silent error
          }
          
          // Trigger refresh in ChatWindow if it's open
          if (window.refreshChatWindow) {
            window.refreshChatWindow();
          }
        }
      }
    });

    // Handle mark_read messages
    const unsubscribeMarkRead = addMessageHandler('mark_read', (data) => {
      if (data.type === 'mark_read' && data.userId) {
        stableMarkMessagesAsReadForUser(data.userId);
      }
    });

    // Also listen for other message types
    const unsubscribeChat = addMessageHandler('chat_message', (data) => {
      // Handle similarly to direct_message
      const messageData = {
        senderId: data.senderId || data.sender_id,
        receiverId: data.receiverId || data.receiver_id,
        messageId: data.messageId || data.id,
        message: data,
        senderName: data.senderName
      };
      unsubscribeDirect({ ...messageData, message: data });
    });

    const unsubscribeNew = addMessageHandler('new_message', (data) => {
      if (data.chatType === 'normal' || !data.sessionId) {
        // Handle as direct message
        const messageData = {
          senderId: data.senderId || data.sender_id,
          receiverId: data.receiverId || data.receiver_id,
          messageId: data.messageId || data.id,
          message: data,
          senderName: data.senderName
        };
        unsubscribeDirect({ ...messageData, message: data });
      }
    });

    return () => {
      unsubscribeDirect();
      unsubscribeChat();
      unsubscribeNew();
      unsubscribeMarkRead();
    };
  }, [isConnected, currentUser, addMessageHandler, wsSendMessage, stableMarkMessagesAsReadForUser, initialUnreadLoaded, t]);

  // FIXED: Load initial unread count on app start - with actual counts
  useEffect(() => {
    const loadInitialUnreadCount = async () => {
      if (!currentUser || !isConnected || initialUnreadLoaded) return;
      
      try {
        // Try to get from localStorage first for immediate display
        const storedUnread = JSON.parse(localStorage.getItem('unreadMessages') || '{}');
        const storedLastReadIds = JSON.parse(localStorage.getItem('lastReadMessageIds') || '{}');
        
        const initialTotal = Object.values(storedUnread).reduce((sum, count) => sum + count, 0);
        if (initialTotal > 0) {
          setGlobalUnreadCount(initialTotal);
          setUserUnreadCounts(storedUnread);
        }
        
        setLastReadMessageIds(storedLastReadIds);
        
        // Then fetch fresh data from API
        const response = await getChatContacts();
        if (response.success && Array.isArray(response.data)) {
          const unreadByUser = {};
          const lastReadIds = {};
          let totalUnreadMessages = 0;
          
          response.data.forEach(contact => {
            const unreadCount = contact.unread || 0;
            if (unreadCount > 0) {
              // Store actual count
              unreadByUser[contact.id] = unreadCount;
              totalUnreadMessages += unreadCount;
            }
            
            // Store last read message ID if available
            if (contact.lastReadMessageId) {
              lastReadIds[contact.id] = contact.lastReadMessageId;
            }
          });
          
          setGlobalUnreadCount(totalUnreadMessages);
          setUserUnreadCounts(unreadByUser);
          setLastReadMessageIds(lastReadIds);
          setInitialUnreadLoaded(true); // Mark initial load as complete
          
          // Update localStorage
          localStorage.setItem('unreadMessages', JSON.stringify(unreadByUser));
          localStorage.setItem('lastReadMessageIds', JSON.stringify(lastReadIds));
        }
      } catch (error) {
        setInitialUnreadLoaded(true); // Mark as loaded even on error to prevent blocking
      }
    };
    
    loadInitialUnreadCount();
  }, [currentUser, isConnected, initialUnreadLoaded]);

  // NEW: Monitor JWT expiration and auto-redirect
  useEffect(() => {
    if (!currentUser || !loginSuccess) return;

    const token = getToken();
    if (!token) return;

    try {
      // Decode JWT to get expiration
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expirationTime = payload.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      const timeUntilExpiry = expirationTime - currentTime;
      
      if (timeUntilExpiry <= 0) {
        // Already expired
        window.handleAuthError(true);
        return;
      }
      
      // Set timeout to fire 10 seconds before actual expiration
      const warningTime = timeUntilExpiry - 10000; // 10 seconds early
      
      const timeoutId = setTimeout(() => {
        window.handleAuthError(true);
      }, Math.max(warningTime, 0));

      return () => clearTimeout(timeoutId);
    } catch (error) {
      window.handleAuthError(true);
    }
  }, [currentUser, loginSuccess]);

  // NEW: Set default page title when institution changes
  useEffect(() => {
    if (selectedInstitution?.name) {
      document.title = `${selectedInstitution.name} - ${t('app.schoolPortal')}`;
    }
  }, [selectedInstitution, t]);

  // Single-device enforcement: when the server sends force_logout (because this
  // account just logged in on another device), clear auth and redirect to login
  // with a reason param so the login page can show an explanatory message.
  useEffect(() => {
    const handleForceLogout = async () => {
      console.warn('🔐 Force logout: clearing session and redirecting to login');
      try {
        await authStore.logout();
      } catch (_) {}
      window.location.href = '/login?reason=other_device';
    };

    window.addEventListener('simpleclass:force_logout', handleForceLogout);
    return () => window.removeEventListener('simpleclass:force_logout', handleForceLogout);
  }, [authStore]);

  // Show banner on login page when redirected after force logout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'other_device') {
      setLoginError(t('app.loggedOutOtherDevice', 'You have been logged out because your account was opened on another device.'));
      // Clean up the URL param without a page reload
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);
    }
  }, [t]);

  // ✅ UPDATED: Separate institution loading from auth initialization with PUBLIC PORTAL LANGUAGE support
  useEffect(() => {
    const initializeApp = async () => {
      // Prevent multiple initializations or running during logout
      if (hasInitialized || isLoggingOut) {
        return;
      }

      setHasInitialized(true);
      
      const subdomain = getSubdomain();
      const isOnLoginPage = window.location.pathname === '/login';
      const isOnResetPage = window.location.pathname.startsWith('/reset-password');
      const isOnInvitationPage = window.location.pathname.startsWith('/complete-invitation-setup');
      const isOnPublicPage = isOnLoginPage || isOnResetPage || isOnInvitationPage;

      if (subdomain) {
        setIsLoadingInstitution(true);
        try {
          const institutionResult = await getInstitutionBySubdomain(subdomain);
          if (institutionResult.success && institutionResult.data) {
            setSelectedInstitution(institutionResult.data);
            localStorage.setItem('selectedInstitution', JSON.stringify(institutionResult.data));
            localStorage.setItem('currentInstitutionId', institutionResult.data.id.toString());

            // ✅ NEW: Set public portal language for public pages
            if (isOnPublicPage) {
              const publicLanguage = institutionResult.data.public_portal_language || 'en'; // Default to 'en'
              console.log('[Public Portal Language] Setting language for public page:', publicLanguage);
              try {
                await i18n.changeLanguage(publicLanguage);
                console.log('[Public Portal Language] Language changed successfully to:', publicLanguage);
              } catch (langError) {
                console.error('[Public Portal Language] Failed to set language, falling back to English:', langError);
                await i18n.changeLanguage('en'); // Fallback to English on error
              }
            }

            // FIXED: Only initialize auth if NOT on public pages and NOT logging out
            if (!isOnPublicPage && !isLoggingOut) {
              const hasValidToken = getToken();
              const hasValidUserData = getUserData();
              
              if (hasValidToken && hasValidUserData && !authStore.isInitialized) {
                try {
                  await authStore.initializeAuth();
                  
                  // Check if initialization was successful
                  if (authStore.isAuthenticated && authStore.user) {
                    setCurrentUser(authStore.user);
                    setCurrentUserRoleInSelectedInstitution(authStore.role);
                    setLoginSuccess(true);
                    
                    // Store auth data for WebSocket
                    localStorage.setItem('userId', authStore.user.id || authStore.user.userId);
                    localStorage.setItem('userRole', authStore.role);
                    localStorage.setItem('authToken', hasValidToken);

                    // ✅ FIXED: Fetch user profile to get language preference from database (overrides public language)
                    console.log('[Language Debug] Fetching user profile for language...');
                    try {
                      const profileResponse = await fetchUserProfileAPI();
                      console.log('[Language Debug] Profile response:', profileResponse);
                      
                      if (profileResponse.success && profileResponse.data?.user) {
                        const dbLanguage = profileResponse.data.user.language;
                        console.log('[Language Debug] Language from database:', dbLanguage);
                        console.log('[Language Debug] Current i18n language:', i18n.language);
                        
                        // Check if language exists and is valid
                        if (dbLanguage && typeof dbLanguage === 'string' && dbLanguage.trim() !== '') {
                          // Set language if different from current
                          if (dbLanguage !== i18n.language) {
                            console.log('[Language Debug] Changing language to:', dbLanguage);
                            await i18n.changeLanguage(dbLanguage);
                            localStorage.setItem('userLanguage', dbLanguage);
                            console.log('[Language Debug] Language changed successfully');
                          } else {
                            console.log('[Language Debug] Language already set correctly');
                          }
                        } else {
                          console.log('[Language Debug] No valid language in database, using fallback');
                          // Fallback to localStorage if database has no language
                          const storedLanguage = localStorage.getItem('userLanguage');
                          if (storedLanguage && storedLanguage !== i18n.language) {
                            console.log('[Language Debug] Using localStorage language:', storedLanguage);
                            await i18n.changeLanguage(storedLanguage);
                          }
                        }
                      } else {
                        console.log('[Language Debug] Profile fetch unsuccessful or no data');
                      }
                    } catch (profileError) {
                      console.error('[Language Debug] Failed to fetch user profile for language:', profileError);
                      
                      // Fallback to localStorage on error
                      const storedLanguage = localStorage.getItem('userLanguage');
                      if (storedLanguage && storedLanguage !== i18n.language) {
                        console.log('[Language Debug] Error fallback - using localStorage:', storedLanguage);
                        await i18n.changeLanguage(storedLanguage);
                      }
                    }

                    // PRE-FETCH CALENDAR DATA FOR CURRENT MONTH
                    try {
                      const now = new Date();
                      const monthStart = startOfMonth(now);
                      const monthEnd = endOfMonth(now);
                      const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                      const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
                      
                      await getCalendarSessions(
                        format(calendarStart, 'yyyy-MM-dd'),
                        format(calendarEnd, 'yyyy-MM-dd')
                      );
                    } catch (calendarError) {
                      // Silent error
                    }
                  } else {
                    removeToken();
                    removeUserData();
                    setCurrentUser(null);
                    setCurrentUserRoleInSelectedInstitution(null);
                    setLoginSuccess(false);
                  }
                } catch (authError) {
                  removeToken();
                  removeUserData();
                  setCurrentUser(null);
                  setCurrentUserRoleInSelectedInstitution(null);
                  setLoginSuccess(false);
                }
              } else {
                if (!hasValidToken || !hasValidUserData) {
                  removeToken();
                  removeUserData();
                }
                setCurrentUser(null);
                setCurrentUserRoleInSelectedInstitution(null);
                setLoginSuccess(false);
              }
            } else {
              setCurrentUser(null);
              setCurrentUserRoleInSelectedInstitution(null);
              setLoginSuccess(false);
            }
          }
        } catch (error) {
          // Silent error
          console.error('[Institution Load] Error loading institution:', error);
        }
        setIsLoadingInstitution(false);
      }

      setIsInitializing(false);
    };

    initializeApp();
  }, [isLoggingOut, authStore, i18n]);

  // FIXED: Set up global auth error handler with guard against double execution
  useEffect(() => {
    let authErrorInProgress = false;
    
    window.handleAuthError = (isTokenExpired = false) => {
      if (authErrorInProgress) {
        return;
      }
      authErrorInProgress = true;
      
      try {
        // Clear local storage first
        removeToken();
        removeUserData();
        localStorage.removeItem('selectedInstitution');
        localStorage.removeItem('currentInstitutionId');
        localStorage.removeItem('userId');
        localStorage.removeItem('userRole');
        localStorage.removeItem('unreadMessages');
        localStorage.removeItem('lastReadMessageIds');
        localStorage.removeItem('userLanguage');
        
        // Safe reset session store
        try {
          resetSessionStore();
        } catch (sessionError) {
          // Silent error
        }
        
        // Store error message if token expired
        if (isTokenExpired) {
          localStorage.setItem('loginError', t('app.sessionExpired'));
        }
        
        // Single hard redirect - this will reload the page and reset all state
        window.location.href = '/login';
      } catch (error) {
        window.location.href = '/login';
      }
    };

    return () => {
      delete window.handleAuthError;
    };
  }, [resetSessionStore, t]);

  const clearLoginError = useCallback(() => {
    setLoginError('');
  }, []);

  const handleEmailChange = useCallback((email) => {
    setLoginEmail(email);
  }, []);

  const handleSignOut = useCallback(() => {
    setIsLoggingOut(true);
    
    setCurrentUser(null);
    setCurrentUserRoleInSelectedInstitution(null);
    setLoginSuccess(false);
    setLoginError('');
    setGlobalUnreadCount(0);
    setUserUnreadCounts({});
    setLastReadMessageIds({});
    setInitialUnreadLoaded(false);
    sessionReadMessagesRef.current.clear();
    
    localStorage.clear();
    sessionStorage.clear();
    
    try {
      document.cookie.split(";").forEach(function(c) { 
        document.cookie = c.replace(/^ +/, "").replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/"); 
      });
    } catch (cookieError) {
      // Silent error
    }
    
    try {
      resetSessionStore();
    } catch (sessionError) {
      // Silent error
    }
    
    window.history.replaceState({}, '', '/login');
    setIsLoggingOut(false);
    setHasInitialized(false);
    
  }, [resetSessionStore]);

  const handleInstitutionSelected = useCallback((institution) => {
    setSelectedInstitution(institution);
    localStorage.setItem('selectedInstitution', JSON.stringify(institution));
    
    const currentSubdomain = getSubdomain();
    if (institution.subdomain && currentSubdomain !== institution.subdomain) {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      const currentPort = port ? `:${port}` : '';
      const baseDomain = hostname.split('.').slice(-2).join('.');
      const newSubdomain = institution.subdomain;
      const newUrl = `${protocol}//${newSubdomain}.${baseDomain}${currentPort}`;
      window.location.href = newUrl;
    }
  }, []);

  const onLoginOrchestration = useCallback(async (email, password) => {
    if (!selectedInstitution || !selectedInstitution.id) {
      const errorMsg = t('app.noInstitutionSelected');
      setLoginError(errorMsg);
      return { success: false, message: errorMsg };
    }
    
    try {
      localStorage.setItem('currentInstitutionId', selectedInstitution.id.toString());
      
      const result = await authStore.login(email, password);
      
      if (result.success) {
        await new Promise(resolve => setTimeout(resolve, 50));
        
        const currentAuthState = useAuthStore.getState();
        const authUser = currentAuthState.user;
        const authRole = currentAuthState.role;
        
        // Store auth data for WebSocket
        localStorage.setItem('userId', authUser.id || authUser.userId);
        localStorage.setItem('userRole', authRole);
        localStorage.setItem('authToken', result.data?.token || getToken());
        
        // ✅ NEW: Set language from login response (user preference overrides public language)
        if (result.data?.language) {
          try {
            console.log('[Login Language] Setting user language from login response:', result.data.language);
            await i18n.changeLanguage(result.data.language);
            localStorage.setItem('userLanguage', result.data.language);
            console.log('[Login Language] User language set successfully');
          } catch (langError) {
            console.error('[Login Language] Failed to set user language:', langError);
            // Fallback to English on error
            await i18n.changeLanguage('en');
          }
        }
        
        setCurrentUser(authUser);
        setCurrentUserRoleInSelectedInstitution(authRole);
        setLoginSuccess(true);
        setLoginEmail(email);
        setLoginError('');
        setGlobalUnreadCount(0);
        setUserUnreadCounts({});
        setLastReadMessageIds({});
        setInitialUnreadLoaded(false);
        sessionReadMessagesRef.current.clear();

        // PRE-FETCH CALENDAR DATA AFTER LOGIN
        try {
          const now = new Date();
          const monthStart = startOfMonth(now);
          const monthEnd = endOfMonth(now);
          const calendarStart = startOfWeek(monthStart, { weekStartsOn: 1 });
          const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
          
          await getCalendarSessions(
            format(calendarStart, 'yyyy-MM-dd'),
            format(calendarEnd, 'yyyy-MM-dd')
          );
        } catch (calendarError) {
          // Silent error
        }
        
        return { success: true };
      } else {
        const errorMsg = result.message || t('app.loginFailed');
        setLoginError(errorMsg);
        return { success: false, message: errorMsg };
      }
    } catch (error) {
      const errorMessage = error.message || t('app.loginFailed');
      setLoginError(errorMessage);
      return { success: false, message: errorMessage };
    }
  }, [selectedInstitution, authStore, t, i18n]);

  const LoginPageWrapper = useMemo(() => {
    if (isLoggingOut) {
      return (
        <div className="flex justify-center items-center min-h-screen bg-slate-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
            <p className="text-slate-600 text-lg">{t('app.signingOut')}</p>
          </div>
        </div>
      );
    }

    const isAuthenticated = loginSuccess && currentUser && currentUserRoleInSelectedInstitution;
    
    if (isAuthenticated) {
      return <Navigate to="/calendar" replace />;
    }
    
    if (!selectedInstitution) {
      if (getSubdomain()) {
        // Check if loading is complete
        if (isLoadingInstitution || isInitializing) {
          // Still loading - show spinner
          return (
            <div className="flex justify-center items-center min-h-screen bg-slate-50">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
                <p className="text-slate-600 text-lg">{t('app.loadingInstitution')}</p>
              </div>
            </div>
          );
        } else {
          // Loading complete but institution not found - show error
          return <InstitutionErrorPage />;
        }
      } else {
        return <Navigate to="/" replace />;
      }
    }
    
    return (
      <InstitutionLogin
        institutionName={selectedInstitution.name}
        onLoginOrchestration={onLoginOrchestration}
        loginError={loginError}
        clearLoginError={clearLoginError}
        initialEmail={loginEmail}
        onEmailChange={handleEmailChange}
      />
    );
  }, [
    isLoggingOut,
    loginSuccess, 
    currentUser, 
    currentUserRoleInSelectedInstitution, 
    selectedInstitution,
    isLoadingInstitution,
    isInitializing,
    onLoginOrchestration, 
    loginError, 
    clearLoginError, 
    loginEmail, 
    handleEmailChange,
    t
  ]);

  const handleJoinSession = useCallback((sessionId) => {
    window.location.href = `/sessions/${sessionId}`;
  }, []);

  const handleCourseSelect = useCallback((courseId) => {
    window.location.href = `/courses/${courseId}/upcoming`;
  }, []);

  // NEW: Robust root redirect component with useNavigate
  const RootRedirect = () => {
    const navigate = useNavigate();
    
    useEffect(() => {
      // Only redirect after initialization is complete
      if (isInitializing || isLoadingInstitution) {
        return;
      }
      
      // If no institution found, stay on error page
      if (!selectedInstitution) {
        return;
      }
      
      // Redirect based on auth state
      if (currentUser && currentUserRoleInSelectedInstitution) {
        navigate('/calendar', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    }, [navigate, isInitializing, isLoadingInstitution, selectedInstitution, currentUser, currentUserRoleInSelectedInstitution]);
    
    // Show loading or error states
    if (isInitializing || isLoadingInstitution) {
      return (
        <div className="flex justify-center items-center min-h-screen bg-slate-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
            <p className="text-slate-600 text-lg">{t('app.loading')}</p>
          </div>
        </div>
      );
    }
    
    if (!selectedInstitution) {
      return <InstitutionErrorPage />;
    }
    
    // Show loading while redirect happens
    return (
      <div className="flex justify-center items-center min-h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto mb-4"></div>
          <p className="text-slate-600 text-lg">{t('app.loading')}</p>
        </div>
      </div>
    );
  };

  return (
    <Router>
      <Routes>
        <Route path="/" element={
          getSubdomain() ? (
            <RootRedirect />
          ) : (
            <SubdomainEntry onInstitutionSelected={handleInstitutionSelected} />
          )
        } />
        
        <Route path="/login" element={LoginPageWrapper} />
        
        <Route path="/complete-invitation-setup" element={<CompleteInvitationSetup />} />
        
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        
        {/* All protected routes with AppLayout */}
        <Route path="/calendar" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <AppLayout
              handleSignOut={handleSignOut}
              currentUser={currentUser}
              currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
              selectedInstitution={selectedInstitution}
              globalUnreadCount={globalUnreadCount}
              onUnreadCountChange={handleUnreadCountChange}
              markMessagesAsReadForUser={stableMarkMessagesAsReadForUser}
              clearUserFromSessionTracking={stableClearUserFromSessionTracking}
            >
              <Calendar 
                handleJoinSession={handleJoinSession}
                currentUserRole={currentUserRoleInSelectedInstitution}
                userRole={currentUserRoleInSelectedInstitution}
                institutionName={selectedInstitution?.name}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/courses" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <AppLayout
              handleSignOut={handleSignOut}
              currentUser={currentUser}
              currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
              selectedInstitution={selectedInstitution}
              globalUnreadCount={globalUnreadCount}
              onUnreadCountChange={handleUnreadCountChange}
              markMessagesAsReadForUser={stableMarkMessagesAsReadForUser}
              clearUserFromSessionTracking={stableClearUserFromSessionTracking}
            >
              <CourseList 
                user={currentUser}
                userRole={currentUserRoleInSelectedInstitution}
                institutionName={selectedInstitution?.name}
                selectedInstitution={selectedInstitution}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route 
          path="/courses/:courseId" 
          element={<Navigate to="upcoming" replace />} 
        />
        
        <Route path="/courses/:courseId/upcoming" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <AppLayout
              handleSignOut={handleSignOut}
              currentUser={currentUser}
              currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
              selectedInstitution={selectedInstitution}
              globalUnreadCount={globalUnreadCount}
              onUnreadCountChange={handleUnreadCountChange}
              markMessagesAsReadForUser={stableMarkMessagesAsReadForUser}
              clearUserFromSessionTracking={stableClearUserFromSessionTracking}
            >
              <CourseSessionsWrapper 
                sessionType="upcoming"
                user={currentUser}
                userRole={currentUserRoleInSelectedInstitution}
                institutionName={selectedInstitution?.name}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/courses/:courseId/completed" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <AppLayout
              handleSignOut={handleSignOut}
              currentUser={currentUser}
              currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
              selectedInstitution={selectedInstitution}
              globalUnreadCount={globalUnreadCount}
              onUnreadCountChange={handleUnreadCountChange}
              markMessagesAsReadForUser={stableMarkMessagesAsReadForUser}
              clearUserFromSessionTracking={stableClearUserFromSessionTracking}
            >
              <CourseSessionsWrapper 
                sessionType="completed"
                user={currentUser}
                userRole={currentUserRoleInSelectedInstitution}
                institutionName={selectedInstitution?.name}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/students" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <AppLayout
              handleSignOut={handleSignOut}
              currentUser={currentUser}
              currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
              selectedInstitution={selectedInstitution}
              globalUnreadCount={globalUnreadCount}
              onUnreadCountChange={handleUnreadCountChange}
              markMessagesAsReadForUser={stableMarkMessagesAsReadForUser}
              clearUserFromSessionTracking={stableClearUserFromSessionTracking}
            >
              <StudentsOverview 
                user={currentUser}
                userRole={currentUserRoleInSelectedInstitution}
                institutionName={selectedInstitution?.name}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        <Route path="/profile" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <AppLayout
              handleSignOut={handleSignOut}
              currentUser={currentUser}
              currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
              selectedInstitution={selectedInstitution}
              globalUnreadCount={globalUnreadCount}
              onUnreadCountChange={handleUnreadCountChange}
              markMessagesAsReadForUser={stableMarkMessagesAsReadForUser}
              clearUserFromSessionTracking={stableClearUserFromSessionTracking}
            >
              <ProfileComponent 
                institutionName={selectedInstitution?.name}
              />
            </AppLayout>
          </ProtectedRoute>
        } />
        
        {/* LiveSessionMain now handles mobile automatically */}
        <Route path="/sessions/:sessionId/whiteboard" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <DetachedWhiteboardPage />
          </ProtectedRoute>
        } />
        
        <Route path="/sessions/:sessionId" element={
          <ProtectedRoute
            isInitializing={isInitializing}
            isLoadingInstitution={isLoadingInstitution}
            selectedInstitution={selectedInstitution}
            currentUser={currentUser}
            currentUserRoleInSelectedInstitution={currentUserRoleInSelectedInstitution}
          >
            <LiveSessionMain 
              institutionName={selectedInstitution?.name}
            />
          </ProtectedRoute>
        } />
        
        <Route path="/dashboard" element={<Navigate to="/calendar" replace />} />
        
        <Route path="*" element={
          <div className="flex justify-center items-center min-h-screen bg-slate-50">
            <div className="text-center p-8">
              <h1 className="text-2xl font-semibold text-slate-800 mb-4">{t('app.pageNotFound.title')}</h1>
              <p className="text-slate-600 mb-6">{t('app.pageNotFound.message')}</p>
              <a href="/calendar" className="text-cyan-600 hover:text-cyan-700 underline">{t('app.pageNotFound.goToCalendar')}</a>
            </div>
          </div>
        } />
      </Routes>
    </Router>
  );
}

// Main App component wrapped with WebSocketProvider
function App() {
  return (
    <WebSocketProvider>
      <AppContent />
    </WebSocketProvider>
  );
}

export default App;